import { Box, Grid } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { parseISO } from 'date-fns';
import { useFormik } from 'formik';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { BackButton } from 'src/components/shared/Button/BackButton';
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { DescriptionList } from 'src/components/shared/DescriptionList/DescriptionList';
import {
  Divider,
  VerticalDivider
} from 'src/components/shared/Divider/Divider';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { StickyFormHeader } from 'src/components/shared/Forms/StickyFormHeader/StickyFormHeader';
import { StatusCircle } from 'src/components/shared/StatusCircle/StatusCircle';
import { Header4 } from 'src/components/shared/Typography/Header4';
import { RootState } from 'src/store';
import { MixtureDetailDto } from 'src/store/mixtures/mixture';
import { selectMixtureStatuses } from 'src/store/mixtures/mixtures.selectors';
import { selectParameterSets } from 'src/store/parametersets/parametersets.selectors';
import { RunSystemDto } from 'src/store/runsystems/runsystem';
import { selectActiveRunSystems } from 'src/store/runsystems/runsystems.selectors';
import { SpecificationDto } from 'src/store/specifications/specification';
import { selectSpecificationsBy } from 'src/store/specifications/specifications.selectors';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  mixture: Nullable<MixtureDetailDto>;
  onCancel: () => void;
  onSubmit: (mixture: MixtureDetailDto) => void;
  onCreateRun: (mixture: MixtureDetailDto, rs: RunSystemDto) => void;
  onDownloadMixtureData: () => void;
};

export const MixtureDetailsForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  onCreateRun,
  ...props
}) => {
  const { t } = useTranslation();

  const [mixture, setMixture] = useState<MixtureDetailDto>({
    mixDate: new Date(),
    mixtureStatusId: 1
  } as MixtureDetailDto);

  const [runSystem, setRunSystem] = useState<Nullable<RunSystemDto>>(null);

  const [showErrors, setShowErrors] = useState<boolean>(false);

  const specifications$ = useSelector((state: RootState) =>
    selectSpecificationsBy(state.specifications, s => !!s.mixtureTypeId)
  );

  const mixtureStatuses$ = useSelector(selectMixtureStatuses);
  const parameterSets$ = useSelector(selectParameterSets);
  const runSystems$ = useSelector(selectActiveRunSystems);

  useEffect(() => {
    if (props.mixture) {
      setMixture({
        ...props.mixture,
        mixDate:
          typeof props.mixture.mixDate === 'string'
            ? parseISO(props.mixture.mixDate)
            : props.mixture.mixDate
      });
    }
  }, [props.mixture]);

  useEffect(() => {
    const runSystem = runSystems$.find(
      x => x.id === props.mixture?.product?.runSystemId
    );
    setRunSystem(runSystem ?? null);
  }, [props.mixture, runSystems$]);

  const validate = (mixture: MixtureDetailDto) => {
    const validationRunners = [
      validationRunner('mixDate', t('mixture.mixDate'), required),
      validationRunner(
        'specificationId',
        t('specification.singular'),
        required
      ),
      validationRunner('parameterSetId', t('parameterSet.singular'), required),
      validationRunner('description', t('props.description'), maxLength(200)),
      validationRunner('mixtureStatusId', t('mixture.status'), required)
    ];
    return runValidations(mixture, validationRunners);
  };

  const SpecificationListItem = (
    props: React.HTMLAttributes<HTMLLIElement>,
    specification: SpecificationDto
  ) => (
    <li {...props}>
      <Grid container>
        <Grid item xs={3}>
          {specification.no}
        </Grid>
        <Grid item xs={5}>
          {specification.name}
        </Grid>
        <Grid item xs={4}>
          {specification.gradeName}
        </Grid>
      </Grid>
    </li>
  );

  const formik = useFormik({
    initialValues: mixture,
    enableReinitialize: true,
    validate: validate,
    onSubmit: (values, { setSubmitting }) => {
      onSubmit(values);
      setSubmitting(false);
    }
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    isSubmitting
  } = formik;

  return (
    <Fragment>
      <StickyFormHeader className="flex align-center jc-sb">
        <DescriptionList>
          <BackButton onClick={onCancel} />
          <li>
            <Header4>{t('props.number')}</Header4>
            {values.no || '-'}
          </li>
          <li>
            <Header4>{t('props.lastUpdated')}</Header4>
            {values.user
              ? `${values.user.firstName} ${values.user.lastName}`
              : '-'}
          </li>
          <li>
            <Header4>{t('specification.singular')}</Header4>
            {values.specification?.name || '-'}
          </li>
          <li>
            <Header4>{t('grade.singular')}</Header4>
            {values.specification?.gradeName || '-'}
          </li>
          <li>
            <Header4>{t('props.count')}</Header4>
            <div className="flex align-center jc-sb">
              {values.currentNumber || '-'}
              <StatusCircle
                color={
                  values.currentNumber &&
                  values.totalNumber &&
                  values.currentNumber >= values.totalNumber
                    ? 'var(--green-status-color-rgb)'
                    : 'var(--yellow-status-color-rgb)'
                }
                className="ml-16"
              />
            </div>
          </li>
          <li>
            <Header4>{t('props.amount')}</Header4>
            <div className="flex align-center jc-sb">
              {values.currentAmount || '-'}
              <StatusCircle
                color={
                  values.currentAmount &&
                  values.totalAmount &&
                  values.currentAmount >= values.totalAmount
                    ? 'var(--green-status-color-rgb)'
                    : 'var(--yellow-status-color-rgb)'
                }
                className="ml-16"
              />
            </div>
          </li>
        </DescriptionList>

        <div className="flex jc-end">
          <TertiaryButton
            onClick={onCancel}
            label={t('cancel')}
            className="mr-32"
          />
          <PrimaryButton
            type="submit"
            label={t('save.verb')}
            disabled={isSubmitting}
            onClick={() => {
              setShowErrors(true);
              handleSubmit();
            }}
          />
        </div>
      </StickyFormHeader>

      <form onSubmit={handleSubmit}>
        <Grid container>
          <Grid item xs={12} md={7}>
            <Grid container>
              <Grid item xs={12} lg={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    className="m-0 mb-24 w100"
                    label={t('mixture.mixDate')}
                    value={values.mixDate}
                    onChange={date => {
                      setFieldValue('mixDate', date);
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} lg={4}>
                <InputField
                  name="totalNumber"
                  type="number"
                  label={t('props.totalCount')}
                  value={values.totalNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.totalNumber}
                  displayError={touched.totalNumber || showErrors}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <InputField
                  name="totalAmount"
                  type="number"
                  label={t('props.totalAmount')}
                  value={values.totalAmount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.totalAmount}
                  displayError={touched.totalAmount || showErrors}
                />
              </Grid>
            </Grid>

            <Autocomplete
              options={specifications$}
              label={t('specification.singular')}
              optionField="name"
              value={values.specification}
              getOptionLabel={option =>
                `${option.no}  |  ${option.name}  |  ${option.gradeName}`
              }
              renderOption={(props, option) =>
                SpecificationListItem(props, option)
              }
              filterOptions={(options, state) =>
                options.filter(o =>
                  `${o.no} ${o.name} ${o.gradeName}`
                    .toLocaleLowerCase()
                    .includes(state.inputValue.toLocaleLowerCase())
                )
              }
              errorMessage={errors.specificationId}
              displayError={!!touched.specification || showErrors}
              onChange={specification => {
                setFieldValue('specificationId', specification?.id);
              }}
              onBlur={handleBlur}
            />
            <InputField
              name="description"
              label={t('props.description')}
              multiline
              rows={4}
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.description}
              displayError={touched.description || showErrors}
            />
          </Grid>
          <Grid item lg={1} className="hide-sm">
            <Box display="flex" justifyContent="center" height="100%">
              <VerticalDivider />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={mixtureStatuses$}
              label={t('mixture.status')}
              optionField="name"
              value={values.mixtureStatus}
              onChange={mixtureStatus => {
                setFieldValue('mixtureStatusId', mixtureStatus?.id);
              }}
              onBlur={handleBlur}
            />

            <Divider className="mb-32" />

            <Autocomplete
              options={runSystems$}
              label={t('runSystem.singular')}
              optionField="name"
              value={runSystem}
              onChange={runSystem => {
                setRunSystem(runSystem);
              }}
              onBlur={handleBlur}
              disabled={!mixture.id}
            />

            <SecondaryButton
              onClick={() => {
                onCreateRun(values, runSystem!);
              }}
              icon="la-plus"
              fullWidth
              className="mb-8"
              disabled={!mixture.id || !runSystem || mixture.productId !== null}
            >
              {`${t('create.verb')} ${t('product.run')}`}
            </SecondaryButton>

            {mixture.product && (
              <DescriptionList>
                <li>
                  <Header4>{t('product.runNo')}</Header4>
                  <Link to={`/products/${mixture.productId}`}>
                    {mixture.product.runNo} {mixture.product.runSystemName}
                  </Link>
                </li>
                <li>
                  <Header4>{t('product.lotNo')}</Header4>
                  {mixture.product.lotNo}
                </li>
              </DescriptionList>
            )}

            <SecondaryButton
              onClick={() => props.onDownloadMixtureData()}
              icon="la-file-excel"
              fullWidth
              disabled={!mixture.id}
            >
              {`${t('mixture.mixturedata')}`}
            </SecondaryButton>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} md={7}>
            <Autocomplete
              options={parameterSets$}
              label={t('parameterSet.singular')}
              optionField="name"
              value={values.parameterSet}
              errorMessage={errors.parameterSetId}
              displayError={!!touched.parameterSetId || showErrors}
              onChange={parameterSet => {
                setFieldValue('parameterSetId', parameterSet?.id);
              }}
              onBlur={handleBlur}
              className="mb-24"
            />
          </Grid>
        </Grid>
      </form>
    </Fragment>
  );
};
